import { Component, DestroyRef, OnInit, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { ActivatedRoute, Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { MessageService } from 'primeng/api';
import { ApsPermitedScreen } from '../../models/aps-permission/aps-permited-screen';
import { ConstantsFordProtect } from '../../models/constants/constants-ford-protect';
import { SaleChannel } from '../../models/enum/sale-channel';
import { StatusChassi } from '../../models/enum/status-chassi';
import { VinValidate } from '../../models/vin-validate/vin-validate';
import { ApsPermissionService } from '../../services/aps-permission/aps-permission.service';
import { SystemCountryService } from '../../services/system-country/system-country.service';
import { VehicleInformationService } from '../../services/vehicle-information/vehicle-information.service';

@Component({
	selector: 'app-vehicle-information',
	templateUrl: './vehicle-information.component.html',
	styleUrls: ['./vehicle-information.component.scss'],
	standalone: false
})
export class VehicleInformationComponent implements OnInit {
	private readonly destroyRef = inject(DestroyRef);

	vinValidate: VinValidate | undefined;
	validationKm: string | undefined;
	enabledOdometro: boolean | undefined;
	dayWarrantyStartDate: string | undefined;
	saleChannel: SaleChannel;
	dateAttachment: string | undefined;
	odometerNull: number | undefined | null;
	systemCountry: string | undefined;
	odometerErrorKey: string | null = null;

	disableStartNullWarranty = false;
	isCtaSuccess = false;
	private odometerDebounceTimer: ReturnType<typeof setTimeout> | null = null;

	constructor(
		private vehicleInformationService: VehicleInformationService,
		private router: Router,
		private route: ActivatedRoute,
		private translateService: TranslateService,
		private messageService: MessageService,
		private apsPermissionService: ApsPermissionService,
		private systemCountryService: SystemCountryService
	) {
		this.saleChannel = route.snapshot.paramMap.get('saleChannel') as SaleChannel;
		this.systemCountryService.setSystemCountry();
	}

	ngOnInit() {
		this.vinValidate = this.vehicleInformationService.getAllChassiValidation();

		if (this.vinValidate === null || this.vinValidate.validated === StatusChassi.invalid) {
			if (this.saleChannel === SaleChannel.EXTERNAL) {
				this.router.navigate(['sales-channel-external'], { skipLocationChange: true, replaceUrl: false });
			} else {
				this.router.navigate(['sales-channel'], { skipLocationChange: true, replaceUrl: false });
			}
		}

		this.apsPermissionService.permissionValidation(ApsPermitedScreen.screenVinValidation)
		this.isValidationKm();
		this.translateService.onLangChange.pipe(
			takeUntilDestroyed(this.destroyRef)
		).subscribe(() => {
			this.updateValidationKmLabel();
		});
		this.getDayWarrantyStartDate();

		this.systemCountry = this.systemCountryService.getSystemCountry();
		this.disableStartNullWarranty = this.systemCountry === ConstantsFordProtect.ARG && this.vinValidate.warrantyStartDate === null;
		if (this.disableStartNullWarranty) {
			this.messageService.add({
				severity: 'error',
				summary: this.translateService.instant('TRANSLATE.ERROR'),
				detail: this.translateService.instant('TRANSLATE.CANNOT.GENERATE.CONTRACT.WARRANTY.START.DATE.PENDING')
			});
		}
	}

	isValidationKm() {
		if (this.vinValidate && this.vinValidate.warrantyStartDate === null) {

			this.vinValidate.odometro = 0;
			this.enabledOdometro = true;

		} else {

			this.enabledOdometro = false;
		}

		this.updateValidationKmLabel();
	}

	private updateValidationKmLabel(): void {
		if (this.enabledOdometro === undefined) {
			return;
		}

		this.validationKm = this.translateService.instant(
			this.enabledOdometro ? 'TRANSLATE.YES' : 'TRANSLATE.NOT'
		);
	}

	buttonComeBack() {
		if (this.saleChannel === SaleChannel.EXTERNAL) {
			this.router.navigate(['sales-channel-external'], { skipLocationChange: true, replaceUrl: false });
		} else {
			this.router.navigate(['sales-channel'], { skipLocationChange: true, replaceUrl: false });
		}
	}

	buttonNext() {
		if (this.hasValidOdometer()) {
			this.router.navigate(['choose-plan', this.saleChannel], { skipLocationChange: true, replaceUrl: false });
		} else {
			this.odometerNull = null;
			this.focusOdometerField();
		}
	}

	onOdometerChange(value: number | null | undefined) {
		const isValid = this.updateOdometerValidationState(value);

		// Reset success state immediately on each change to avoid premature focus
		this.isCtaSuccess = false;

		if (this.odometerDebounceTimer !== null) {
			clearTimeout(this.odometerDebounceTimer);
			this.odometerDebounceTimer = null;
		}

		if (isValid) {
			// Debounce: only activate success state after user stops typing
			this.odometerDebounceTimer = setTimeout(() => {
				this.isCtaSuccess = true;
				this.odometerDebounceTimer = null;
			}, 700);
		}
	}

	private hasValidOdometer(): boolean {
		const odometerValue = this.vinValidate?.odometro;
		return this.updateOdometerValidationState(odometerValue);
	}

	private updateOdometerValidationState(value: number | null | undefined): boolean {
		if (value === null || value === undefined) {
			this.odometerNull = null;
			this.odometerErrorKey = 'TRANSLATE.VEHICLE.INFORMATION.ODOMETER.REQUIRED';
			return false;
		}

		if (value < 0) {
			this.odometerNull = null;
			this.odometerErrorKey = 'TRANSLATE.VEHICLE.INFORMATION.ODOMETER.NON.NEGATIVE';
			return false;
		}

		if (this.enabledOdometro === false && value <= 0) {
			this.odometerNull = null;
			this.odometerErrorKey = 'TRANSLATE.VEHICLE.INFORMATION.ODOMETER.USED.GREATER.THAN.ZERO';
			return false;
		}

		this.odometerNull = undefined;
		this.odometerErrorKey = null;
		return true;
	}

	private focusOdometerField(): void {
		const odometerInput = document.getElementById('withoutgrouping') as HTMLElement | null;
		if (!odometerInput) {
			return;
		}

		odometerInput.scrollIntoView({ behavior: 'smooth', block: 'center' });
		odometerInput.focus();
		setTimeout(() => odometerInput.focus(), 60);
	}

	/*
		Método para transformar a data:
		1 - Adiciona um zero a esquerda, caso o mes seja de 1 a 9;
		2 - Retorna o getUTCDate(), para garantir que o dia é o mesmo
		que vem do backend;
		3 - Assincrono pois dependendo do momento ele ainda não tem a data;
	*/
	async getDayWarrantyStartDate() {
		this.dayWarrantyStartDate = "";

		if (this.vinValidate && this.vinValidate.warrantyStartDate !== null) {
			this.dayWarrantyStartDate = this.transformDate(new Date(this.vinValidate.warrantyStartDate));
		}
	}

	transformDate(date: Date): string {
		const newDateStart = new Date(date);
		return ((("0") + newDateStart.getDate()).toString().slice(-2)) + ((("0") + ((newDateStart.getMonth() + 1))).toString().slice(-2)) + newDateStart.getFullYear().toString();
	}

	newDate() {

		if (!this.vinValidate || !this.vinValidate.warrantyStartDate) {
			this.dateAttachment = '';
			return this.dateAttachment;
		}

		const dateStart = new Date(this.vinValidate.warrantyStartDate + 'T00:00:00');
		const numberDate = String(dateStart.getUTCDate()).padStart(2, '0');
		const numberMonth = dateStart.getUTCMonth() + 1;
		const numberYear = dateStart.getUTCFullYear();
		let nameMonth = '';

		switch (numberMonth) {

			case 1:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JANUARY');
				break;

			case 2:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.FEBRUARY');
				break;

			case 3:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.MARCH');
				break;

			case 4:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.APRIL');
				break;

			case 5:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.MAY');
				break;

			case 6:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JUNE');
				break;

			case 7:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.JULY');
				break;

			case 8:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.AUGUST');
				break;

			case 9:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.SEPTEMBER');
				break;

			case 10:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.OCTOBER');
				break;

			case 11:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.NOVEMBER');
				break;

			case 12:
				nameMonth = this.translateService.instant('TRANSLATE.MONTH.DECEMBER');
				break;

			default:
				return this.dateAttachment = '';

		}

		return this.dateAttachment = numberDate + "/" + nameMonth + "/" + numberYear;

	}

}